import { UIFilterStarterKindType } from '@mm/state/model/ChatStarterModel';
import classNames from 'classnames';
import React, { useCallback } from 'react';
import {
  ButtonColor,
  PrimaryButton,
} from '../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { useConnectPageHandlers } from '../../../../../../common/hooks/use-connect-page-handlers';
import { Banner } from '@mm/components';
import { facebookRelatedStarterKinds } from '../types';
import commentGuardSrc from './connect-page-art-comment-guard-min.png';
import fbWelcomerSrc from './connect-page-art-fb-welcomer-min.png';
import landingPageSrc from './connect-page-art-landing-pages-min.png';
import shareLinksSrc from './connect-page-art-share-links-min.png';
import styles from './ConnectPageBanner.module.css';

type Props = {
  kind: UIFilterStarterKindType;
  className?: string;
};

const wideImageClassName = classNames(styles.wide, styles.center);
const narrowImageClassName = classNames(styles.narrow, styles.bottom);

const map: Partial<
  Record<
    UIFilterStarterKindType,
    {
      title: string;
      checkpoints: string[];
      image: string;
      imageClassName?: string;
    }
  >
> = {
  comment_guard: {
    title:
      'Connect your Facebook account to start generating leads from comments on your FB posts:',
    checkpoints: [
      'Automatically message users who commented on your FB posts',
      'Users who respond to your message will become new Messenger leads',
      'Re-engage with these contacts later with relevant promotional messages',
    ],
    image: commentGuardSrc,
    imageClassName: wideImageClassName,
  },
  facebook_optin: {
    title:
      'Connect your Facebook account to start using Facebook opt-in elements on your website:',
    checkpoints: [
      'Connect your chat dialogues to a Checkbox Plugin or Send-to-Messenger button',
      'Contacts who click through the FB opt-ins on your website will become new leads',
      'Re-engage with these contacts later with relevant promotional messages',
    ],
    image: shareLinksSrc,
    imageClassName: narrowImageClassName,
  },
  facebook_page_welcomer: {
    title:
      'Connect your Facebook account to start generating leads from your FB page:',
    checkpoints: [
      'Minimize response times by having your bot handle incoming messages on your FB page',
      'Set bots to qualify leads & notify you when a conversation requires your attention',
      'Re-engage with these contacts later with relevant promotional messages',
    ],
    image: fbWelcomerSrc,
    imageClassName: wideImageClassName,
  },
  share_links: {
    title:
      'Connect your Facebook account to start generating leads using a Share Link:',
    checkpoints: [
      'Build custom links that contacts can click through to connect with you on Messenger ',
      'Customize different bot responses for different links',
      'Re-engage with these contacts later with relevant promotional messages',
    ],
    image: shareLinksSrc,
    imageClassName: narrowImageClassName,
  },
  landing_pages: {
    title:
      'Connect your Facebook account to start building a Messenger landing page:',
    checkpoints: [
      'Build a landing page that users can visit to learn more about your business',
      'Users can click though a CTA button to connect with you on Messenger',
      'Re-engage with these contacts later with relevant promotional messages',
    ],
    image: landingPageSrc,
    imageClassName: wideImageClassName,
  },
};

export const ConnectPageBanner = ({ kind, className }: Props) => {
  const { connect } = useConnectPageHandlers();
  const onSubmitButtonClick = useCallback(connect, [connect]);

  const match = facebookRelatedStarterKinds.includes(kind);
  const content = map[kind];

  if (!match || !content) {
    return null;
  }

  const { title, checkpoints, image, imageClassName } = content;

  return (
    <Banner
      css={{
        container: classNames(className, styles.container),
        content: styles.content,
        left: styles.left,
        right: styles.right,
      }}
      title={title}
      checkpointsText={checkpoints}
      button={
        <PrimaryButton color={ButtonColor.green} onClick={onSubmitButtonClick}>
          Connect
        </PrimaryButton>
      }
    >
      <div className={imageClassName}>
        <img src={image} className={styles.image} alt={title} />
      </div>
    </Banner>
  );
};
